<template>
    <div class="withdrawal-page">
        <SectionHeader :icon="`icon-withdrawal`">
            {{$t('withdrawal') | upperCase}}
        </SectionHeader>
        <div
            v-if="isLoading"
            class="spinner-container"
        >
            <SpinnerBars/>
        </div>
        <div v-else-if="user.isVerifiedIdCard && hasDepositedAlready">
            <div class="top-container">
                <Balance
                    :title="$t('available amount')"
                    :amount="userBalance"
                    :stakeInActiveTickets="stakeInActiveTickets"
                    :isStakeLoading="areTicketsLoading"
                />
                <Bonus :amount="userBonus"/>
            </div>
            <div>
                <WithdrawalList
                    :isOnlineEligible="isOnlineEligible"
                    :isShopEligible="isShopEligible"
                    :isBankTransferEligible="isBankTransferEligible"
                    :sortedShops="sortedShops()"
                />
            </div>
            <div class="help-section">
                <div class="section-title mb-2">
                    {{ $t('need help to make a withdrawal') | capitalize }}?
                </div>
                <div class="notice mb-4">
                    {{ `${$t('if you have any problems with withdrawal don\'t hesitate to contact us')}!`
                        | capitalize }}
                </div>
                <StaticLink
                    tag="button"
                    class="btn__primary btn__icon--left btn--lg btn--w-90"
                    path="help-url"
                    label="help"
                >
                    <i class="icon icon__lg icon-help_circle btn__icon" />
                </StaticLink>
            </div>
        </div>
    </div>
</template>

<script>
    import {
        keyBy as _keyBy,
    } from '@lodash';
    import { mapGetters, mapActions } from 'vuex';
    import Modals from '@store/modules/ui/shared/modal/modals';
    import { WithdrawalType } from '@models/user/enums';
    import UserService from '@core/services/user/UserService';
    import ClientStorage from '@core/utils/storage/ClientStorage';
    import AuthenticationError from '@core/errors/authentication/AuthenticationError';
    import { errorGuard } from '@app/utils';
    import { isIdCardVerificationPending } from '@models/user/UserDataService';
    import WithdrawalList from './components/WithdrawalList';
    import Bonus from '../components/Bonus.vue';
    import Balance from '../components/Balance.vue';

    export default {
        name: 'WithdrawalContainer',
        data() {
            return {
                isLoading: true,
                asyncErrors: {},
                withdrawalAbility: {},
                hasDepositedAlready: false,
                sessionHasExpired: false,
            };
        },
        created() {
            this.WithdrawalType = WithdrawalType;

            errorGuard({
                action: async () => {
                    this.hasDepositedAlready = await this.checkIfUserHasAnyDeposits();

                    if (this.sessionHasExpired) {
                        return;
                    }

                    if (this.user.isVerifiedIdCard && this.hasDepositedAlready) {
                        this.openWithdrawal();
                    } else if (this.hasDepositedAlready && isIdCardVerificationPending(this.user)) {
                        this.setModal(Modals.idCardVerification.code);
                    } else {
                        this.openWithdrawalRequirements();
                    }
                }
            });
        },
        computed: {
            ...mapGetters('data/authentication', [
                'user',
                'userBalance',
                'userBonus',
            ]),
            ...mapGetters('data/country', [
                'shops',
            ]),
            ...mapGetters('data/tickets', [
                'stakeInActiveTickets',
                'areTicketsLoading',
            ]),
            isShopEligible() {
                return this.withdrawalAbility && this.withdrawalAbility.isShopEligible;
            },
            isOnlineEligible() {
                return this.withdrawalAbility && this.withdrawalAbility.isOnlineEligible;
            },
            isBankTransferEligible() {
                return this.withdrawalAbility && this.withdrawalAbility.isBankTransferEligible;
            },
        },
        methods: {
            ...mapActions('data/authentication', [
                'sessionExpired',
            ]),
            ...mapActions('ui/modal', [
                'setModal',
            ]),
            ...mapActions('data/country', [
                'fetchShops',
            ]),
            ...mapActions('errors', [
                'showError',
            ]),
            async fetchServerData(callback) {
                this.isLoading = true;
                await errorGuard({
                    action: async () => {
                        try {
                            await callback();
                        } catch (e) {
                            if (e instanceof AuthenticationError) {
                                this.sessionExpired();
                                this.sessionHasExpired = true;
                                return;
                            }
                            throw e;
                        }
                    }
                });
                this.isLoading = false;
            },
            async checkIfUserHasAnyDeposits() {
                await this.fetchServerData(async () => {
                    this.withdrawalAbility = await UserService.getInstance().checkWithdrawalAbility();
                });

                return [
                    this.withdrawalAbility.isOnlineEligible,
                    this.withdrawalAbility.isShopEligible,
                    this.withdrawalAbility.isBankTransferEligible,
                ].some(Boolean);
            },
            openWithdrawal() {
                const willBonusesExpire = this.withdrawalAbility.willAnyBonusExpire
                    && this.withdrawalAbility.bonusesToExpire.length;

                if (willBonusesExpire) {
                    this.setModal({
                        code: Modals.withdrawalBonusesThatCanExpire.code,
                        data: {
                            bonuses: this.withdrawalAbility.bonusesToExpire,
                        }
                    });
                }

                this.fetchServerData(this.fetchShops);
            },
            openWithdrawalRequirements() {
                this.setModal({
                    code: Modals.withdrawalIntro.code,
                    data: {
                        hasIdCardIntro: !this.user.isVerifiedIdCard,
                        hasDepositIntro: !this.hasDepositedAlready
                    }
                });
            },
            /**
             * Uses last N shops from local storage if any, and insert them at the beginning
             * All the other shops are sorted alphabetically without local storage shops
             */
            sortedShops() {
                const savedIds = ClientStorage.get('users:withdrawal:shops');

                if (!savedIds || savedIds.length === 0) {
                    return this.shops;
                }

                const savedIdsMap = _keyBy(savedIds);
                const shopsMap = _keyBy(this.shops, 'comTradeId');
                const savedShops = savedIds
                    .filter((id) => !!shopsMap[id])
                    .map((id) => shopsMap[id]);

                return [
                    ...savedShops,
                    ...this.shops.filter((shop) => !savedIdsMap[shop.comTradeId]),
                ];
            },
        },
        components: {
            WithdrawalList,
            Bonus,
            Balance,
        }
    };
</script>

<style>
    .withdrawal_modal {
        width: 800px;
        max-width: 800px;
        max-height: 1400px;
    }

    .unavailable {
        color: lightgrey;
    }
</style>



// WEBPACK FOOTER //
// src/app/modules/userProfile/withdrawal/WithdrawalContainer.vue